import React from 'react';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ResultDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  closeLabel: string;
  header: React.ReactNode;
  children: React.ReactNode;
}

export default function ResultDialogShell({
  open,
  onOpenChange,
  closeLabel,
  header,
  children,
}: ResultDialogShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-6 pt-6 pb-4">{header}</DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        <DialogFooter className="border-t px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SectionHeaderProps {
  label: string;
  count?: number;
  tooltip?: string;
}

export function SectionHeader({ label, count, tooltip }: SectionHeaderProps) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </h3>
      {tooltip && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help text-muted-foreground/70" aria-label="info" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {count !== undefined && (
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
          {count}
        </Badge>
      )}
    </div>
  );
}

export interface CompetencyEntry {
  keyword: string;
  score?: number | null;
  // Share of the overall match; the competencies sum to the result's match value.
  contribution?: number | null;
  // Raw 0–1 match quality, drives the badge color.
  quality?: number | null;
}

export function CompetencyBadges({ items }: { items: (CompetencyEntry | string)[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((entry, idx) => {
        const keyword = typeof entry === 'string' ? entry : entry.keyword;
        // Number shown is the contribution to the overall match (falls back to the
        // raw score for older payloads); color reflects the match quality.
        const display =
          typeof entry === 'string' ? null : entry.contribution ?? entry.score ?? null;
        const quality =
          typeof entry === 'string' ? null : entry.quality ?? entry.score ?? null;
        const scoreClass =
          quality == null
            ? ''
            : quality >= 0.7
            ? 'text-green-700'
            : quality >= 0.5
            ? 'text-amber-600'
            : 'text-red-600';
        return (
          <Badge key={idx} variant="info" className="px-2 py-0.5 text-xs font-medium">
            {keyword}
            {display != null && (
              <sup className={cn('ml-0.5 text-[0.6em] font-bold', scoreClass)}>
                {display.toFixed(2)}
              </sup>
            )}
          </Badge>
        );
      })}
    </div>
  );
}
